import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { SERVICE_DETAILS } from "@/data/serviceDetails";

/**
 * ServiceCatalog — 14 dedicated service detail pages (/sluzba/:slug).
 *
 * Rendered on the landing page after the main <Services /> grid so both
 * humans and crawlers can discover every SEO-optimised subpage. Cards are
 * intentionally compact — the full storytelling lives on each subpage.
 */
export default function ServiceCatalog() {
  return (
    <section
      id="specializacie"
      className="py-24 lg:py-32 bg-black particles"
      data-testid="service-catalog"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-14 max-w-3xl">
          <span className="mono text-xs text-[#FF5E00] uppercase tracking-[0.2em]">
            14 špecializácií · Fixné ceny · Kalkulačka na každej stránke
          </span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mt-4">
            Presne to, čo <span className="text-[#00E5FF] neon-cyan">potrebujete</span>
            <span className="text-[#FF5E00]">.</span>
          </h2>
          <p className="text-zinc-400 mt-4 text-lg font-light leading-relaxed">
            Každá špecializácia má vlastnú stránku s cenníkom, postupom, kalkulačkou
            a FAQ — vrátane transparentného rozpisu čo je v cene a čo si klient rieši sám.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SERVICE_DETAILS.map((svc, i) => {
            const accentText = svc.accent === "orange" ? "text-[#FF5E00]" : "text-[#00E5FF]";
            const cheapest = svc.packages[0]?.priceFrom;
            return (
              <motion.div
                key={svc.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: (i % 6) * 0.05, duration: 0.4 }}
              >
                <Link
                  to={`/sluzba/${svc.slug}`}
                  data-testid={`service-catalog-card-${svc.slug}`}
                  className="group relative block h-full rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:bg-white/[0.05] hover:border-white/20 transition-all"
                >
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <div className={`mono text-[10px] uppercase tracking-widest ${accentText} mb-1`}>
                        {svc.hero.pretitle.split("·")[0].trim()}
                      </div>
                      <h3 className="text-lg font-black text-white leading-tight">
                        {svc.hero.h1.replace(/\.$/, "")}
                      </h3>
                    </div>
                    <ArrowUpRight size={18} className="text-zinc-500 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed mb-4 line-clamp-2">
                    {svc.hero.h1Accent}
                  </p>
                  <div className="flex items-center justify-between pt-3 border-t border-white/5">
                    <div className="flex items-center gap-1.5">
                      <Sparkles size={12} className={accentText} />
                      <span className="text-xs text-zinc-500">od</span>
                      <span className={`text-sm font-black ${accentText}`}>{cheapest}</span>
                    </div>
                    <span className="text-[10px] mono text-zinc-600 uppercase tracking-widest truncate max-w-[140px]">
                      /sluzba/{svc.slug}
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <p className="text-center text-xs text-zinc-500 mt-8">
          Nevidíte svoju službu? Napíšte nám — robíme kompletné stavby aj individuálne zákazky.
        </p>
      </div>
    </section>
  );
}
